import { Link } from 'react-router';
import { ArrowLeft, Route as RouteIcon } from 'lucide-react';
import { usePathProgress } from '~/lib/path-progress';
import type { PathContext } from '#shared/types/public';

/* Het lint boven het artikel: "je leest stap 3 van 11 van dit leerpad".
   Staat in de tekstbaan en NIET in de zijbalk — die krijgt lg:hidden zodra de
   lezer de inhoudsopgave wegklikt, en dan zou juist voor hem de leerpadcontext
   verdwijnen.

   "Stap 3 van 11" komt uit de loader en staat er dus meteen; alleen de vulling
   en het aantal afgeronde stappen wachten op de hydratatie, want die komen uit
   localStorage. Vandaar dat er vóór die tijd geen NUL staat maar niets: een
   getal dat een tel later verspringt leest als een storing. */

export function PathRail({ ctx }: { ctx: PathContext }) {
  const { isDone, ready } = usePathProgress(ctx.id);
  const pathHref = `/leerpad/${encodeURIComponent(ctx.slug)}#stap-${ctx.index + 1}`;

  // Zonder ?pad= volgt de lezer dit pad niet aantoonbaar — hij kwam via zoeken
  // of een link binnen. Dan alleen een vriendelijke wegwijzer: geen balk
  // onderaan, en niets dat zichzelf afvinkt.
  if (!ctx.active) {
    return (
      <aside className="mb-5 flex flex-wrap items-center gap-x-2 gap-y-1 rounded-lg border border-hairline bg-surface px-4 py-2.5 text-base text-ink-2 shadow-sm">
        <RouteIcon size={16} className="shrink-0 text-accent" aria-hidden />
        <span>
          Dit artikel is stap {ctx.index + 1} van {ctx.total} in het leerpad{' '}
          <Link viewTransition to={pathHref} className="font-semibold text-ink underline underline-offset-2 hover:text-accent-ink">
            {ctx.title}
          </Link>
          {ctx.alsoIn > 0 && (
            <span className="text-ink-3">
              {' '}(en in {ctx.alsoIn} {ctx.alsoIn === 1 ? 'ander leerpad' : 'andere leerpaden'})
            </span>
          )}
        </span>
        <Link
          viewTransition
          to={`/artikel/${encodeURIComponent(ctx.steps[ctx.index]!.slug)}?pad=${encodeURIComponent(ctx.slug)}`}
          className="ms-auto shrink-0 rounded-md border border-hairline-strong px-3 py-1 text-sm font-medium text-ink-2 transition hover:border-accent hover:text-ink"
        >
          Leerpad volgen
        </Link>
      </aside>
    );
  }

  const doneCount = ready ? ctx.steps.filter((s) => isDone(s.articleId)).length : 0;
  const pct = ctx.total ? Math.round((doneCount / ctx.total) * 100) : 0;

  return (
    <section aria-label="Leerpad" className="mb-5 rounded-lg border border-hairline bg-surface px-4 py-3 shadow-sm">
      <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1">
        <Link
          viewTransition
          to={pathHref}
          className="flex min-w-0 items-center gap-2 text-base font-semibold text-ink transition hover:text-accent-ink"
        >
          <ArrowLeft size={15} className="shrink-0 text-accent" aria-hidden />
          <span className="truncate">{ctx.title}</span>
        </Link>
        <span className="shrink-0 text-sm text-ink-3 tabular-nums">
          Stap {ctx.index + 1} van {ctx.total}
          {ready && doneCount > 0 && ` · ${doneCount} afgerond`}
        </span>
      </div>
      <div
        className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-sunken"
        role="progressbar"
        aria-label={`Voortgang leerpad ${ctx.title}`}
        aria-valuemin={0}
        aria-valuemax={ctx.total}
        // Weglaten terwijl we het nog niet weten is de juiste manier om
        // "onbepaald" te zeggen — niet stiekem een 0 tonen.
        aria-valuenow={ready ? doneCount : undefined}
        aria-valuetext={ready ? `${doneCount} van ${ctx.total} stappen afgerond` : undefined}
        aria-busy={ready ? undefined : true}
      >
        <div
          className="h-full rounded-full bg-gradient-to-r from-accent-logo to-orange-400 motion-safe:transition-[width] motion-safe:duration-500"
          style={{ width: `${pct}%` }}
        />
      </div>
    </section>
  );
}
